import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../layouts/Header";
import Navbar from "../layouts/Navbar";
import Swal from "sweetalert2";
import ImageFreeCropper from "../crop_image/ImageFreeCropper";

function PelaporanEdit() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formValue, setFormValue] = useState({
        deskripsi: "",
        foto_cover: "",
        foto_wbs: "",
        dokumen: "", // bisa berupa URL lama atau file baru
    });

    const [selectedFiles, setSelectedFiles] = useState({
        foto_cover: null,
        foto_wbs: null,
        dokumen: null,
    });

    const [preview, setPreview] = useState({
        foto_cover: null,
        foto_wbs: null,
        dokumen: null,
    });

    const [cropData, setCropData] = useState({
        isOpen: false,
        imageSrc: null,
        imageType: "",
    });

    const [croppedImage, setCroppedImage] = useState(null);

    // === FETCH DATA DARI API ===
    const fetchData = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/");
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pelaporan/readPelaporanById/${id}`,
                { headers: { authorization: token } }
            );

            const data = response.data;

            setFormValue({
                deskripsi: data.deskripsi || "",
                foto_cover: data.foto_cover || "",
                foto_wbs: data.foto_wbs || "",
                dokumen: data.dokumen || "",
            });
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // === OPEN IMAGE CROPPER ===
    const openCropper = (file, imageType) => {
        const reader = new FileReader();
        reader.onload = () => {
            setCropData({
                isOpen: true,
                imageSrc: reader.result,
                imageType,
            });
        };
        reader.readAsDataURL(file);
    };

    const closeCropper = () => {
        setCropData({
            isOpen: false,
            imageSrc: null,
            imageType: "",
        });
    };

    const applyCrop = () => {
        if (croppedImage instanceof Blob) {
            const blobUrl = URL.createObjectURL(croppedImage);

            setFormValue((prev) => ({
                ...prev,
                [cropData.imageType]: blobUrl,
            }));

            setPreview((prev) => ({
                ...prev,
                [cropData.imageType]: blobUrl,
            }));

            setSelectedFiles((prev) => ({
                ...prev,
                [cropData.imageType]: new File([croppedImage], `${cropData.imageType}.jpg`, {
                    type: "image/jpeg",
                }),
            }));
        } else {
            console.warn("⚠️ Cropped image belum berupa Blob");
        }

        closeCropper();
    };

    // === HANDLE HASIL CROP ===
    const handleCropped = (file, previewUrl) => {
        setSelectedFiles((prev) => ({
            ...prev,
            [cropData.imageType]: file,
        }));

        setPreview((prev) => ({
            ...prev,
            [cropData.imageType]: previewUrl,
        }));

        closeCropper();
    };

    // === HANDLE INPUT CHANGE ===
    const handleChange = (event) => {
        const { name, value, files } = event.target;

        if (files && files.length > 0) {
            const file = files[0];

            // kalau file PDF → langsung simpan, gak lewat cropper
            if (name === "dokumen") {
                const blobUrl = URL.createObjectURL(file);
                setSelectedFiles((prev) => ({ ...prev, dokumen: file }));
                setPreview((prev) => ({ ...prev, dokumen: blobUrl }));
                return;
            }

            // kalau image → buka cropper
            openCropper(file, name);
        } else {
            setFormValue((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    // === HANDLE SUBMIT ===
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formValue.deskripsi) {
            Swal.fire({
                title: "Error",
                text: "Deskripsi harus diisi",
                icon: "error",
            });
            return;
        }

        const formData = new FormData();
        formData.append("deskripsi", formValue.deskripsi);

        if (selectedFiles.foto_cover) formData.append("foto_cover", selectedFiles.foto_cover);
        if (selectedFiles.foto_wbs) formData.append("foto_wbs", selectedFiles.foto_wbs);
        if (selectedFiles.dokumen) formData.append("dokumen", selectedFiles.dokumen);

        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/pelaporan/updatePelaporan/${id}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            Swal.fire({
                title: "Success",
                text: "Data berhasil diperbarui",
                icon: "success",
            });
            navigate("/pelaporan");
        } catch (error) {
            console.error("Error updating data:", error);
            const msg = error.response?.data?.message || "Data gagal diperbarui";
            Swal.fire({
                title: "Error",
                text: msg,
                icon: "error",
            });
        }
    };

    const coverSrc = preview.foto_cover
        ? preview.foto_cover
        : formValue.foto_cover
        ? formValue.foto_cover + "?t=" + Date.now()
        : "";

    // === RENDER ===
    return (
        <div className="wrapper">
            <Header />
            <Navbar />

            <div className="content-wrapper">
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Administrasi</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item">
                                        <Link className="text-decoration-none" to="/">
                                            Administrasi
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active">Edit Pelaporan</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Edit Pelaporan</h3>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            {/* Foto Cover */}
                                            <div className="form-group">
                                                <label>Foto Banner</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    name="foto_cover"
                                                    accept="image/*"
                                                    onChange={handleChange}
                                                />
                                                {coverSrc && (
                                                    <img
                                                        src={coverSrc}
                                                        alt="Cover Preview"
                                                        style={{
                                                            maxWidth: "200px",
                                                            marginTop: "10px",
                                                            borderRadius: "5px",
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            {/* Foto WBS */}
                                            <div className="form-group">
                                                <label>Foto WBS</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    name="foto_wbs"
                                                    accept="image/*"
                                                    onChange={handleChange}
                                                />
                                                {(preview.foto_wbs || formValue.foto_wbs) && (
                                                    <img
                                                        src={preview.foto_wbs || formValue.foto_wbs}
                                                        alt="WBS Preview"
                                                        style={{
                                                            maxWidth: "200px",
                                                            marginTop: "10px",
                                                            borderRadius: "5px",
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            {/* Dokumen PDF */}
                                            <div className="form-group">
                                                <label>Dokumen (PDF)</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    name="dokumen"
                                                    accept="application/pdf"
                                                    onChange={handleChange}
                                                />
                                                {selectedFiles.dokumen ? (
                                                    <div className="mt-2">
                                                        <p className="mb-1">
                                                            File baru:{" "}
                                                            <b>{selectedFiles.dokumen.name}</b>
                                                        </p>
                                                        <a
                                                            href={preview.dokumen}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-info"
                                                        >
                                                            Lihat Dokumen Baru
                                                        </a>
                                                    </div>
                                                ) : formValue.dokumen ? (
                                                    <div className="mt-2">
                                                        <a
                                                            href={formValue.dokumen}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-primary"
                                                        >
                                                            Lihat Dokumen Lama
                                                        </a>
                                                    </div>
                                                ) : null}
                                            </div>

                                            {/* Deskripsi */}
                                            <div className="form-group">
                                                <label>Deskripsi</label>
                                                <textarea
                                                    className="form-control"
                                                    name="deskripsi"
                                                    placeholder="Masukkan Deskripsi"
                                                    onChange={handleChange}
                                                    value={formValue.deskripsi}
                                                    rows="4"
                                                />
                                            </div>

                                            {/* Tombol Aksi */}
                                            <button type="submit" className="btn btn-info">
                                                Simpan
                                            </button>
                                            <Link to="/pelaporan">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary ml-2"
                                                >
                                                    Batal
                                                </button>
                                            </Link>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Modal Cropper */}
            {cropData.isOpen && (
                <ImageFreeCropper
                    imageSrc={cropData.imageSrc}
                    setCroppedImage={handleCropped}
                    onClose={closeCropper}
                />
            )}
        </div>
    );
}

export default PelaporanEdit;
